import { useEffect, useMemo, useState } from 'react';
import { Container, KeenIcon } from '@/components';
import {
  Toolbar,
  ToolbarHeading,
  ToolbarPageTitle,
  ToolbarDescription,
  ToolbarActions,
} from '@/partials/toolbar';
import { Editor } from '@tinymce/tinymce-react';
import { TINYMCE_SCRIPT_SRC, fixTinyMceBodyAria } from '@/constant';
import {
  useGetEmailLayoutsQuery,
  useUpdateEmailLayoutMutation,
} from '@/services/EmailLayoutService';
import {
  EmailLayout,
  UpdateEmailLayoutPayload,
} from '@/types/email-layout.types';
import { useParams, useNavigate } from 'react-router-dom';


// Layout HTML is authored in TinyMCE and may contain <img> tags without alt text.
const withImageAlt = (html: string): string =>
  html.replace(/<img\b(?![^>]*\balt\s*=)/gi, '<img alt=""');

const LANGS = ['en', 'fi', 'de', 'sv'] as const;
type Section = 'header' | 'body' | 'footer';

const EmailLayoutView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isFetching } = useGetEmailLayoutsQuery({ page: 1, limit: 100 });
  const [updateLayout, { isLoading: isSaving }] =
    useUpdateEmailLayoutMutation();
  // New UI states for improved UX
  const [activeSection, setActiveSection] = useState<Section>('header');
  const [layoutId, setLayoutId] = useState<string | null>(null);
  const [sectionModes, setSectionModes] = useState<{
    header: 'view' | 'edit';
    body: 'view' | 'edit';
    footer: 'view' | 'edit';
  }>({ header: 'view', body: 'view', footer: 'view' });

  // Local state for each section & language
  const [headers, setHeaders] = useState<
    Record<(typeof LANGS)[number], string>
  >({
    en: '',
    fi: '',
    de: '',
    sv: '',
  });
  const [footers, setFooters] = useState<
    Record<(typeof LANGS)[number], string>
  >({
    en: '',
    fi: '',
    de: '',
    sv: '',
  });
  const [wrappers, setWrappers] = useState<
    Record<(typeof LANGS)[number], string>
  >({
    en: '',
    fi: '',
    de: '',
    sv: '',
  });

  useEffect(() => {
    // data?.data can either be an object with { layouts: EmailLayout[] } or directly EmailLayout[]
    const raw = data?.data;
    let list: EmailLayout[] | undefined;
    if (
      raw &&
      typeof raw === 'object' &&
      !Array.isArray(raw) &&
      'layouts' in raw
    ) {
      list = raw.layouts;
    } else if (Array.isArray(raw)) {
      list = raw as EmailLayout[];
    }

    // Find the layout by ID
    const layout: EmailLayout | undefined = list?.find((l) => l.id === id);

    if (!layout) return;
    setLayoutId(layout.id);
    setHeaders({
      en: layout.header || '',
      fi: layout.header_fi || '',
      de: layout.header_de || '',
      sv: layout.header_sv || '',
    });
    setFooters({
      en: layout.footer || '',
      fi: layout.footer_fi || '',
      de: layout.footer_de || '',
      sv: layout.footer_sv || '',
    });
    setWrappers({
      en: layout.body_wrapper || '',
      fi: layout.body_wrapper_fi || '',
      de: layout.body_wrapper_de || '',
      sv: layout.body_wrapper_sv || '',
    });
  }, [data, id]);

  const getSectionLabel = (section: Section) => {
    if (section === 'header') return 'Header';
    if (section === 'body') return 'Body Wrapper';
    return 'Footer';
  };

  const headerTitle = useMemo(() => {
    const secLabel = getSectionLabel(activeSection);
    const isEditing = sectionModes[activeSection] === 'edit';
    return isEditing ? `Editing ${secLabel}` : `Email Layout: ${secLabel}`;
  }, [activeSection, sectionModes]);

  const scheduleTinyMceA11yFixes = (
    editor: {
      getContainer: () => HTMLElement | null;
    },
    section: Section,
    lng: (typeof LANGS)[number]
  ) => {
    setTimeout(() => {
      const container = editor.getContainer();
      if (container) {
        // Fix accessibility issues with TinyMCE elements
        // Wait for statusbar to be rendered

        // Fix resize handle: add role to div with aria-label
        const resizeHandle = container.querySelector(
          '[data-mce-name="resize-handle"]'
        ) as HTMLElement;
        if (resizeHandle) {
          resizeHandle.setAttribute('role', 'button');
        }

        // Fix statusbar navigation: add unique aria-label
        const statusbarNav = container.querySelector(
          '.tox-statusbar__path[role="navigation"]'
        ) as HTMLElement;
        if (statusbarNav) {
          statusbarNav.setAttribute(
            'aria-label',
            `Editor path navigation for ${getSectionLabel(section).toLowerCase()} ${lng.toUpperCase()} language`
          );
        }
      }
    }, 100);
  };

  const handleSaveSection = async (section: Section) => {
    try {
      if (!layoutId) return;
      let body: UpdateEmailLayoutPayload = {};
      if (section === 'header') {
        body = {
          header: headers.en,
          header_fi: headers.fi,
          header_de: headers.de,
          header_sv: headers.sv,
        };
      } else if (section === 'footer') {
        body = {
          footer: footers.en,
          footer_fi: footers.fi,
          footer_de: footers.de,
          footer_sv: footers.sv,
        };
      } else if (section === 'body') {
        body = {
          body_wrapper: wrappers.en,
          body_wrapper_fi: wrappers.fi,
          body_wrapper_de: wrappers.de,
          body_wrapper_sv: wrappers.sv,
        };
      }
      await updateLayout({ id: layoutId, body }).unwrap();
      setSectionModes((s) => ({ ...s, [section]: 'view' }));
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <>
      <Container>
        <Toolbar>
          <ToolbarHeading>
            <ToolbarPageTitle
              id="template-layout-management-heading"
              text="Template Layout Management"
            />
            <ToolbarDescription>{headerTitle}</ToolbarDescription>
          </ToolbarHeading>
          <ToolbarActions>
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => navigate('/template-layout-management')}
            >
              <KeenIcon icon="arrow-left" /> Back to List
            </button>
            {sectionModes[activeSection] === 'view' ? (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() =>
                  setSectionModes((s) => ({ ...s, [activeSection]: 'edit' }))
                }
              >
                <KeenIcon type="filled" icon="pencil" /> Edit{' '}
                {activeSection === 'body'
                  ? 'Body Wrapper'
                  : activeSection.charAt(0).toUpperCase() +
                    activeSection.slice(1)}
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() =>
                    setSectionModes((s) => ({ ...s, [activeSection]: 'view' }))
                  }
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => handleSaveSection(activeSection)}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
          </ToolbarActions>
        </Toolbar>
      </Container>

      <Container>
        <div className="card">
          <div className="card-body space-y-10">
            {isFetching && (
              <div className="text-gray-600">Loading layout...</div>
            )}

            {/* Section Tabs */}
            <div className="flex flex-wrap gap-2">
              {(
                [
                  { key: 'header', label: 'Header' },
                  { key: 'body', label: 'Body Wrapper' },
                  { key: 'footer', label: 'Footer' },
                ] as ReadonlyArray<{ key: Section; label: string }>
              ).map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveSection(tab.key)}
                  className={`px-4 py-2 rounded-md border text-sm ${
                    activeSection === tab.key
                      ? 'bg-primary text-white border-primary'
                      : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Language Pills - removed per request to show all languages in Header tab */}

            {/* Active Section Content */}
            <div className="space-y-3">
              {activeSection === 'header' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Header
                  </h2>
                  {LANGS.map((lng) => (
                    <div key={`header-${lng}`} className="space-y-3">
                      <h3 className="text-base font-semibold text-gray-800">
                        {lng.toUpperCase()} Header
                      </h3>
                      {sectionModes.header === 'view' ? (
                        <div
                          className="prose max-w-none border rounded-md p-3 bg-white"
                          dangerouslySetInnerHTML={{
                            __html: withImageAlt(headers[lng] || ''),
                          }}
                        />
                      ) : (
                        <Editor
                          tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
                          id={`layout-header-${lng}`}
                          value={headers[lng]}
                          onEditorChange={(content: string) =>
                            setHeaders((s) => ({ ...s, [lng]: content }))
                          }
                          aria-label={`Email layout header editor for ${lng.toUpperCase()} language`}
                          init={{
                            height: 300,
                            menubar: false,
                            statusbar: true,
                            plugins: [
                              'lists',
                              'link',
                              'autolink',
                              'table',
                              'paste',
                              'wordcount',
                              'preview',
                              'image',
                              'code',
                            ],
                            toolbar:
                              'undo redo | bold italic underline forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | table link preview',
                            content_style:
                              'body { font-family: Arial, sans-serif; font-size:14px } .mce-content-body pre { white-space: break-spaces; }',
                            paste_data_images: true,
                            branding: false,
                            aria_label: `Email layout header editor for ${lng.toUpperCase()} language`,
                            setup: (editor: {
                              on: (event: string, callback: () => void) => void;
                              getContainer: () => HTMLElement | null;
                            }) => {
                              fixTinyMceBodyAria(editor);
                              editor.on('init', () =>
                                scheduleTinyMceA11yFixes(editor, 'header', lng)
                              );
                            },
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {activeSection === 'body' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Body Wrapper
                  </h2>
                  {LANGS.map((lng) => (
                    <div key={`wrapper-${lng}`} className="space-y-3">
                      <h3 className="text-base font-semibold text-gray-800">
                        {lng.toUpperCase()} Body Wrapper
                      </h3>
                      {sectionModes.body === 'view' ? (
                        <div
                          className="prose max-w-none border rounded-md p-3 bg-white"
                          dangerouslySetInnerHTML={{
                            __html: withImageAlt(wrappers[lng] || ''),
                          }}
                        />
                      ) : (
                        <Editor
                          tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
                          id={`layout-wrapper-${lng}`}
                          value={wrappers[lng]}
                          onEditorChange={(content: string) =>
                            setWrappers((s) => ({ ...s, [lng]: content }))
                          }
                          aria-label={`Email layout body wrapper editor for ${lng.toUpperCase()} language`}
                          init={{
                            height: 300,
                            menubar: false,
                            statusbar: true,
                            plugins: [
                              'lists',
                              'link',
                              'autolink',
                              'table',
                              'paste',
                              'wordcount',
                              'preview',
                              'image',
                              'code',
                            ],
                            toolbar:
                              'undo redo | bold italic underline forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | table link preview',
                            content_style:
                              'body { font-family: Arial, sans-serif; font-size:14px } .mce-content-body pre { white-space: break-spaces; }',
                            paste_data_images: true,
                            branding: false,
                            aria_label: `Email layout body wrapper editor for ${lng.toUpperCase()} language`,
                            setup: (editor: {
                              on: (event: string, callback: () => void) => void;
                              getContainer: () => HTMLElement | null;
                            }) => {
                              fixTinyMceBodyAria(editor);
                              editor.on('init', () =>
                                scheduleTinyMceA11yFixes(editor, 'body', lng)
                              );
                            },
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {activeSection === 'footer' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Footer
                  </h2>
                  {LANGS.map((lng) => (
                    <div key={`footer-${lng}`} className="space-y-3">
                      <h3 className="text-base font-semibold text-gray-800">
                        {lng.toUpperCase()} Footer
                      </h3>
                      {sectionModes.footer === 'view' ? (
                        <div
                          className="prose max-w-none border rounded-md p-3 bg-white"
                          dangerouslySetInnerHTML={{
                            __html: withImageAlt(footers[lng] || ''),
                          }}
                        />
                      ) : (
                        <Editor
                          tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
                          id={`layout-footer-${lng}`}
                          value={footers[lng]}
                          onEditorChange={(content: string) =>
                            setFooters((s) => ({ ...s, [lng]: content }))
                          }
                          aria-label={`Email layout footer editor for ${lng.toUpperCase()} language`}
                          init={{
                            height: 300,
                            menubar: false,
                            statusbar: true,
                            plugins: [
                              'lists',
                              'link',
                              'autolink',
                              'table',
                              'paste',
                              'wordcount',
                              'preview',
                              'image',
                              'code',
                            ],
                            toolbar:
                              'undo redo | bold italic underline forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | table link preview',
                            content_style:
                              'body { font-family: Arial, sans-serif; font-size:14px } .mce-content-body pre { white-space: break-spaces; }',
                            paste_data_images: true,
                            branding: false,
                            aria_label: `Email layout footer editor for ${lng.toUpperCase()} language`,
                            setup: (editor: {
                              on: (event: string, callback: () => void) => void;
                              getContainer: () => HTMLElement | null;
                            }) => {
                              fixTinyMceBodyAria(editor);
                              editor.on('init', () =>
                                scheduleTinyMceA11yFixes(editor, 'footer', lng)
                              );
                            },
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default EmailLayoutView;
